import { AFDataObject, AFMessageType } from '.';

export interface HistoricalDataPoint {
  mcap: number;
  timestamp: Date;
}

export interface PriceTrackerRawData {
  mcap: number;
  timestamp: Date;
  ath_mcap: number;
  ath_ts: Date;
  historical: {
    '1h': HistoricalDataPoint | null;
    '3h': HistoricalDataPoint | null;
    '6h': HistoricalDataPoint | null;
    '12h': HistoricalDataPoint | null;
    '24h': HistoricalDataPoint | null;
    '3d': HistoricalDataPoint | null;
  };
}

export interface PriceTrackerStateData extends PriceTrackerRawData {
  lastUpdated: Date;
}


export class PriceTracker implements AFDataObject<PriceTrackerRawData, PriceTrackerStateData> {
  public mcap: number;
  public timestamp: Date;
  public ath_mcap: number;
  public ath_ts: Date;
  public historical: {
    '1h': HistoricalDataPoint | null;
    '3h': HistoricalDataPoint | null;
    '6h': HistoricalDataPoint | null;
    '12h': HistoricalDataPoint | null;
    '24h': HistoricalDataPoint | null;
    '3d': HistoricalDataPoint | null;
  };
  public lastUpdated: Date;

  constructor(data?: PriceTrackerRawData | PriceTrackerStateData | null, lastUpdated?: Date) {
    // 기본값 설정
    this.mcap = data?.mcap || 0;
    this.timestamp = data?.timestamp ? new Date(data.timestamp) : new Date(0);
    this.ath_mcap = data?.ath_mcap || 0;
    this.ath_ts = data?.ath_ts ? new Date(data.ath_ts) : new Date(0);
    this.historical = {
      '1h': this.parseHistoricalDataPoint(data?.historical?.['1h']),
      '3h': this.parseHistoricalDataPoint(data?.historical?.['3h']),
      '6h': this.parseHistoricalDataPoint(data?.historical?.['6h']),
      '12h': this.parseHistoricalDataPoint(data?.historical?.['12h']),
      '24h': this.parseHistoricalDataPoint(data?.historical?.['24h']),
      '3d': this.parseHistoricalDataPoint(data?.historical?.['3d']),
    };
    this.lastUpdated = new Date(lastUpdated || 0);
  }

  static create(data?: PriceTrackerRawData | PriceTrackerStateData | null, lastUpdated?: Date): PriceTracker {
    return new PriceTracker(data, lastUpdated);
  }

  getMessageType(): AFMessageType {
    return AFMessageType.PRICE_TRACKER;
  }

  /**
   * Historical data point를 파싱합니다.
   * null이거나 undefined인 경우 null을 반환하고,
   * 객체인 경우 timestamp를 Date 객체로 변환합니다.
   */
  private parseHistoricalDataPoint(
    data: { mcap: number; timestamp: Date | string } | null | undefined
  ): HistoricalDataPoint | null {
    if (!data) {
      return null;
    }
    return {
      mcap: data.mcap,
      timestamp: data.timestamp instanceof Date ? data.timestamp : new Date(data.timestamp),
    };
  }

  toRawData(): PriceTrackerRawData {
    return {
      mcap: this.mcap,
      timestamp: new Date(this.timestamp),
      ath_mcap: this.ath_mcap,
      ath_ts: new Date(this.ath_ts),
      historical: {
        '1h': this.historical['1h'] ? {
          mcap: this.historical['1h'].mcap,
          timestamp: new Date(this.historical['1h'].timestamp),
        } : null,
        '3h': this.historical['3h'] ? {
          mcap: this.historical['3h'].mcap,
          timestamp: new Date(this.historical['3h'].timestamp),
        } : null,
        '6h': this.historical['6h'] ? {
          mcap: this.historical['6h'].mcap,
          timestamp: new Date(this.historical['6h'].timestamp),
        } : null,
        '12h': this.historical['12h'] ? {
          mcap: this.historical['12h'].mcap,
          timestamp: new Date(this.historical['12h'].timestamp),
        } : null,
        '24h': this.historical['24h'] ? {
          mcap: this.historical['24h'].mcap,
          timestamp: new Date(this.historical['24h'].timestamp),
        } : null,
        '3d': this.historical['3d'] ? {
          mcap: this.historical['3d'].mcap,
          timestamp: new Date(this.historical['3d'].timestamp),
        } : null,
      },
    };
  }

  toStateData(): PriceTrackerStateData {
    return {
      ...this.toRawData(),
      lastUpdated: this.lastUpdated,
    };
  }

  filter(existingData: PriceTrackerStateData): boolean {
    return Number(this.lastUpdated) > Number(existingData.lastUpdated);
  }
}
